import { useAuthState } from '~/composables/useAuthState'
import { getApiClient } from '~/utils/api'
import type { MyChallenge } from './competition-types'

export function useDailyChallenge() {
  const { user, isAuthReady, isAuthenticated, fetchAndStoreToken } = useAuthState()
  const { session } = useSession(), { locale } = useI18n()
  const identity = computed(() => isAuthReady.value && isAuthenticated.value && user.value?.id && session.value?.id ? `${user.value.id}:${session.value.id}` : '')
  const data = shallowRef<MyChallenge>(), loading = ref(false), mounted = ref(false)
  let revision = 0, controller: AbortController | undefined
  async function load() {
    const request = ++revision, owner = identity.value, language = locale.value
    controller?.abort(); data.value = undefined; loading.value = false
    if (!mounted.value || !owner) return
    controller = new AbortController()
    const signal = controller.signal
    const current = () => request === revision && owner === identity.value && language === locale.value && !signal.aborted
    loading.value = true
    try {
      const token = await fetchAndStoreToken()
      if (!token || !current()) return
      const response = await getApiClient().post('music/v1/challenge/daily/me', {}, { signal, headers: { Authorization: `Bearer ${token}` }, musicmintInlineError: true, validateStatus: () => true })
      const result = response.data?.data
      if (current() && response.status === 200 && response.data?.code === 200 && Array.isArray(result?.entries)
        && result.entries.every((entry: any) => typeof entry.song_id === 'string' && typeof entry.title === 'string' && Number.isFinite(entry.listeners_today) && entry.listeners_today >= 0)
        && Number.isFinite(result.min_song_listeners) && result.min_song_listeners > 0) data.value = { ...result, entries: result.entries.slice(0, 20) }
    } catch { /* Missing deployment, 404 and errors all show the publish CTA. */ }
    finally { if (current()) loading.value = false }
  }
  watch([identity, locale, mounted], load, { flush: 'sync' })
  onMounted(() => { mounted.value = true })
  onBeforeUnmount(() => { revision++; controller?.abort() })
  return { data, loading, isAuthReady, isAuthenticated }
}
